import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { UserCircle, CheckCircle, X } from "lucide-react";
import Register from "@/components/Register";
import PasswordChange from "@/components/PasswordChange";
import TwoFactorChallenge from "@/components/TwoFactorChallenge";
import TwoFactorEnrollment from "@/components/TwoFactorEnrollment";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [mustEnroll2fa, setMustEnroll2fa] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setRegisteredEmail(null);
    setLoading(true);
    try {
      console.log("[Login] Attempting login for user:", username);
      console.log("[Login] Cookies before login:", document.cookie);

      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });

      console.log("[Login] Login response status:", res.status);
      console.log("[Login] Login response headers:", {
        'set-cookie': res.headers.get('set-cookie'),
        'content-type': res.headers.get('content-type'),
      });
      console.log("[Login] Cookies after login:", document.cookie);

      if (res.ok) {
        const loginData = await res.json();
        console.log("[Login] Login successful:", loginData);

        // Check if user needs to change password first
        if (loginData.requiresPasswordChange) {
          console.log("[Login] User must change password");
          setTempPassword(password); // Save the temp password for the change-password API
          setShowPasswordChange(true);
          return;
        }

        // 2FA branches — pendingAuth is parked on the session; user must verify
        if (loginData.requiresTwoFactor) {
          console.log("[Login] 2FA challenge required");
          setRequiresTwoFactor(true);
          return;
        }

        // mustEnroll2fa is set after a superadmin reset (M4) — user is fully
        // authenticated but cannot proceed until enrolled.
        if (loginData.mustEnroll2fa) {
          console.log("[Login] User must enroll in 2FA");
          setMustEnroll2fa(true);
          return;
        }

        // After login, detect role and send user to the correct portal
        try {
          console.log("[Login] Fetching user info...");
          const who = await fetch("/api/auth/user", { credentials: "include" }).then(r => {
            console.log("[Login] User info response status:", r.status);
            return r.ok ? r.json() : null;
          });
          // Determine destination role — prioritize actual role over field presence
          // Users with clientId (employer/admin) should land on employer portal even if role is "employee"
          let role: "employee" | "employer" | "broker" = "broker";
          if (who?.role === "employer") role = "employer";
          else if (who?.clientId) role = "employer";
          else if (who?.role === "employee" || who?.employeeId) role = "employee";

          // Persist selection so the app enforces allowed routes and hides switcher where needed
          try {
            localStorage.setItem("userRole", role);
            localStorage.setItem("selectedRole", role);
          } catch {}

          const url = new URL(window.location.origin + "/");
          url.searchParams.set("role", role);
          window.location.replace(url.toString());
        } catch {
          // Fallback to reload if anything goes wrong
          window.location.reload();
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Invalid credentials");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackFromRegister = (email?: string) => {
    setShowRegister(false);
    if (email) {
      setRegisteredEmail(email);
      setUsername(email); // Pre-fill the username field
    }
  };

  // Handle successful password change - redirect to dashboard
  const handlePasswordChangeSuccess = async () => {
    try {
      const who = await fetch("/api/auth/user", { credentials: "include" }).then(r =>
        r.ok ? r.json() : null
      );

      let role: "employee" | "employer" | "broker" = "broker";
      if (who?.employeeId || who?.role === "employee") role = "employee";
      else if (who?.clientId || who?.role === "employer") role = "employer";

      try {
        localStorage.setItem("userRole", role);
        localStorage.setItem("selectedRole", role);
      } catch {}

      const url = new URL(window.location.origin + "/");
      url.searchParams.set("role", role);
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  };

  if (showPasswordChange) {
    return (
      <PasswordChange
        currentPassword={tempPassword}
        onSuccess={handlePasswordChangeSuccess}
      />
    );
  }

  if (requiresTwoFactor) {
    return (
      <TwoFactorChallenge
        onSuccess={handlePasswordChangeSuccess}
        onCancel={() => {
          setRequiresTwoFactor(false);
          setUsername("");
          setPassword("");
        }}
      />
    );
  }

  if (mustEnroll2fa) {
    return (
      <TwoFactorEnrollment
        open={true}
        onOpenChange={() => {}}
        onComplete={handlePasswordChangeSuccess}
        forced
      />
    );
  }

  const devRegisterEnabled = import.meta.env.VITE_DEV_REGISTER === "true";
  const signUpUrl = import.meta.env.VITE_SIGNUP_URL ?? "https://administr.com/pricing";

  if (devRegisterEnabled && showRegister) {
    return <Register onBack={handleBackFromRegister} />;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="bg-white dark:bg-gray-900 p-8 rounded shadow-md w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          <UserCircle className="h-12 w-12 text-[#1737A2] dark:text-blue-400 mb-2" />
          <h2 className="text-2xl font-bold text-[#1737A2] dark:text-blue-400 mb-1">Sign In</h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm">Access your administr dashboard</p>
        </div>
        {registeredEmail && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
              <span className="text-sm text-green-800">
                Account created for <span className="font-medium">{registeredEmail}</span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => setRegisteredEmail(null)}
              className="text-green-600 hover:text-green-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
            <input
              type="text"
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1737A2]"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              autoFocus
              autoComplete="username"
              data-testid="login-username"
            />
          </div>
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
              <Link href="/forgot-password" className="text-xs text-[#1737A2] hover:underline">
                Forgot password?
              </Link>
            </div>
            <PasswordInput
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1737A2]"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              />
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <Button
            type="submit"
            variant="default"
            className="w-full flex items-center gap-2 justify-center bg-[#1737A2] hover:bg-[#102366]"
            disabled={loading}
          >
            {loading ? "Signing In..." : (<><UserCircle className="h-5 w-5" />Sign In</>)}
          </Button>
        </form>
        <div className="mt-4 text-center">
          {devRegisterEnabled ? (
            <button
              type="button"
              className="text-[#1737A2] hover:underline text-sm"
              onClick={() => setShowRegister(true)}
            >
              Don't have an account? Register
            </button>
          ) : (
            <a
              href={signUpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1737A2] hover:underline text-sm"
            >
              Don't have an account? Register
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
